import Debug from 'debug';
import * as React from 'react';
import { FormControl, ListGroup } from 'react-bootstrap';
import { Option } from 'gossamer-unified';
import { useEventListener } from '../ancillary/KeyboardEventListener';
import styles from './AutoCompleter.module.css';

const debug = Debug('gfe/fi/AutoCompleterMemory');

interface AutocompleterMemoryProps {
  className?: string;
  clearOnSelection?: boolean;
  label?: string;
  mandatory: boolean;
  onItemSelected: (newValue: string) => void;
  options: Option[];
  placeholder?: string;
  validity?: boolean;
  value: string;
}

export const AutoCompleterMemory: React.FC<AutocompleterMemoryProps> = (props) => {
  const [active, setActive] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>(props.value || '');
  React.useEffect(() => {
    setSearchTerm(props.label || '');
  }, [props.label]);
  const onClose = () => {
    setSearchTerm('');
    setActive(false);
  };
  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debug(`onInputChange(${event.target.value || ''})`);
    setSearchTerm(event.target.value || '');
    setActive(true);
  };
  const onItemSelected = (newValue: string, newLabel: string) => {
    debug(`onItemSelected(${newValue})`);
    setSearchTerm(newLabel);
    setActive(false);
    if (props.clearOnSelection) {
      setSearchTerm('');
    }
    props.onItemSelected(newValue);
  };
  const className = styles.AutoCompleter + (props.className ? ' ' + props.className : '');
  const matchOptions = props.options.filter(
    (item) => item.active !== false && item.label.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1,
  );
  return (
    <FormControl as="div" className={className}>
      <input
        autoComplete="off"
        onChange={onInputChange}
        placeholder={props.placeholder}
        type="text"
        value={searchTerm}
      />
      {searchTerm?.length > 2 && active && (
        <AutoCompleterSub onClose={onClose} onItemSelected={onItemSelected} options={matchOptions} />
      )}
    </FormControl>
  );
};

interface AutoCompleterSubProps {
  existingValue?: string;
  onClose: () => void;
  onItemSelected: (newValue: string, newLabel: string) => void;
  options: Option[];
}

const AutoCompleterSub: React.FC<AutoCompleterSubProps> = (props) => {
  debug(`AutoCompleterSub`);
  debug(props);
  const [highlightRow, setHighlightRow] = React.useState<number>();
  useEventListener(
    'keydown',
    React.useCallback(
      (event: KeyboardEvent) => {
        debug(`inside keyboard listener`);
        if (props.options.length <= (highlightRow ?? -1)) {
          setHighlightRow(undefined);
        }
        if (props.options.length === 0) {
          return;
        }
        if (event.key === 'ArrowDown') {
          if (highlightRow === undefined || highlightRow < props.options.length - 1) {
            setHighlightRow(highlightRow === undefined ? 0 : highlightRow + 1);
          }
        } else if (event.key === 'ArrowUp') {
          if (highlightRow !== undefined) {
            setHighlightRow(highlightRow === 0 ? undefined : highlightRow - 1);
          }
        } else if (event.key === 'Enter') {
          if (highlightRow !== undefined) {
            const item = props.options[highlightRow];
            debug(`calling onItemSelected`);
            props.onItemSelected(item.id, item.label);
          }
        } else if (event.key === 'Escape') {
          props.onClose();
        }
      },
      [highlightRow, props.options?.length],
    ),
  );
  return (
    <ListGroup className={styles.AutoResults} variant="flush">
      {props.options.map((item, index: number) => {
        const label = item.label;
        return (
          <ListGroup.Item
            action
            active={item.id === props.existingValue || index === highlightRow}
            key={item.id}
            onClick={props.onItemSelected.bind(null, item.id, label)}
          >
            {label}
          </ListGroup.Item>
        );
      })}
      {props.options.length === 0 && <ListGroup.Item key="~none">no matches</ListGroup.Item>}
    </ListGroup>
  );
};
